import * as React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, MinusCircle } from 'lucide-react';
import {
  humanVerifyCategoryLabel,
  humanVerifyMachineLabel,
  humanVerifySeverityLabel,
  humanVerifyStatusLabel,
  humanVerifyMachineClasses as machineClass,
  humanVerifySeverityClasses as severityClass,
  humanVerifyStatusClasses as statusClass,
  type HumanVerifyItem,
  type HumanVerifyItemStatus,
  type HumanVerifySeverity,
  type HumanVerifyState,
} from '@/lib/humanVerify';
import { cn } from '@/lib/utils';

interface HumanVerifyChecklistSectionProps {
  humanVerify: HumanVerifyState | null | undefined;
  pendingItemId?: string | null;
  reviewerAvailable?: boolean;
  onResolve?: (item: HumanVerifyItem, status: Extract<HumanVerifyItemStatus, 'verified' | 'dismissed'>, note?: string) => void;
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function SeverityIcon({ severity }: { severity: HumanVerifySeverity }) {
  if (severity === 'block') return <AlertCircle className="h-3.5 w-3.5" />;
  if (severity === 'warn') return <AlertTriangle className="h-3.5 w-3.5" />;
  return <Info className="h-3.5 w-3.5" />;
}

function StatusIcon({ status }: { status: HumanVerifyItemStatus }) {
  if (status === 'verified') return <CheckCircle2 className="h-3.5 w-3.5" />;
  if (status === 'dismissed') return <MinusCircle className="h-3.5 w-3.5" />;
  return <AlertTriangle className="h-3.5 w-3.5" />;
}

function Chip({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium', className)}>
      {children}
    </span>
  );
}

export function HumanVerifyChecklistSection({
  humanVerify,
  pendingItemId,
  reviewerAvailable = true,
  onResolve,
}: HumanVerifyChecklistSectionProps) {
  const items = humanVerify?.items ?? [];
  const unresolvedCount = items.filter((item) => item.status === 'needs_review').length;
  const [notesById, setNotesById] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    setNotesById(Object.fromEntries(items.map((item) => [item.id, item.note ?? ''])));
  }, [items]);

  return (
    <section aria-labelledby="human-verify-heading" className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="human-verify-heading" className="text-sm font-semibold text-foreground">
            Reviewer Sign-off
          </h2>
          <p className="text-xs text-muted-foreground">
            Review the article checks that need human judgment.
          </p>
        </div>
        <Chip className={unresolvedCount > 0 ? 'border-coral/30 bg-coral-light text-coral-dark' : 'border-teal/30 bg-teal-light text-teal-dark'}>
          {unresolvedCount} unverified
        </Chip>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-teal/30 bg-teal-light px-3 py-2 text-sm text-teal-dark">
          No flagged reviewer checks for this article.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const isPending = pendingItemId === item.id;
            const note = notesById[item.id] ?? '';
            const actionsDisabled = isPending || !reviewerAvailable || !onResolve;
            return (
              <li key={item.id} className="rounded-md border border-border bg-warm-bg p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {humanVerifyCategoryLabel(item.category)}
                      </span>
                      <Chip className={severityClass(item.severity)}>
                        <SeverityIcon severity={item.severity} />
                        {humanVerifySeverityLabel(item.severity)}
                      </Chip>
                      <Chip className={machineClass(item.machine_status)}>
                        {humanVerifyMachineLabel(item.machine_status)}
                      </Chip>
                    </div>
                    <p className="text-sm font-medium text-foreground">{item.prompt}</p>
                  </div>
                  <Chip className={statusClass(item.status)}>
                    <StatusIcon status={item.status} />
                    {humanVerifyStatusLabel(item.status)}
                  </Chip>
                </div>

                {item.machine_context && (
                  <div className="mt-2 rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground">
                    {item.machine_context}
                  </div>
                )}

                {(item.verified_by || item.verified_at) && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {item.verified_by || 'Reviewer'} at {formatDateTime(item.verified_at)}
                  </div>
                )}

                <div className="mt-3 grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Note
                    <textarea
                      aria-label={`Note for ${item.prompt}`}
                      value={note}
                      onChange={(event) =>
                        setNotesById((current) => ({ ...current, [item.id]: event.target.value }))
                      }
                      className="min-h-16 rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coral"
                      placeholder="Optional note"
                    />
                  </label>
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      disabled={actionsDisabled || item.status === 'dismissed'}
                      onClick={() => onResolve?.(item, 'dismissed', note.trim() || undefined)}
                      className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      disabled={actionsDisabled || item.status === 'verified'}
                      onClick={() => onResolve?.(item, 'verified', note.trim() || undefined)}
                      className="inline-flex h-9 items-center justify-center rounded-md bg-teal px-3 text-sm font-semibold text-secondary-foreground hover:bg-teal-hover disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                    >
                      Verify
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default HumanVerifyChecklistSection;
